/**
 * Global toast notification system — sağ alt köşe kartları.
 * Kullanım:
 *   const { showToast } = useToast();
 *   showToast('Emir Reddedildi', 'Yetersiz bakiye', 'error');
 */
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { X, AlertTriangle, CheckCircle, Info, XCircle } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ToastType = 'error' | 'warning' | 'success' | 'info';

export interface Toast {
  id:      number;
  type:    ToastType;
  title:   string;
  message: string;
  /** ms — default 6000 */
  duration?: number;
}

interface ToastContextValue {
  showToast: (title: string, message: string, type?: ToastType, duration?: number) => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be inside ToastProvider');
  return ctx;
}

// ─── Single Toast card ────────────────────────────────────────────────────────

function ToastCard({ toast, onRemove }: { toast: Toast; onRemove: (id: number) => void }) {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const duration = toast.duration ?? 3000;

  // Mount → slide in
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  // Auto-dismiss
  useEffect(() => {
    const t = setTimeout(() => dismiss(), duration);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);

  const dismiss = () => {
    setLeaving(true);
    setTimeout(() => onRemove(toast.id), 300);
  };

  const styles: Record<ToastType, { bar: string; icon: React.ReactNode; ring: string }> = {
    error: {
      bar:  'bg-rose-500',
      ring: 'ring-rose-500/30',
      icon: <XCircle size={16} className="text-rose-400 flex-shrink-0 mt-0.5" />,
    },
    warning: {
      bar:  'bg-amber-400',
      ring: 'ring-amber-400/30',
      icon: <AlertTriangle size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />,
    },
    success: {
      bar:  'bg-emerald-500',
      ring: 'ring-emerald-500/30',
      icon: <CheckCircle size={16} className="text-emerald-400 flex-shrink-0 mt-0.5" />,
    },
    info: {
      bar:  'bg-blue-400',
      ring: 'ring-blue-400/30',
      icon: <Info size={16} className="text-blue-400 flex-shrink-0 mt-0.5" />,
    },
  };

  const s = styles[toast.type];

  return (
    <div
      className={`
        relative w-80 rounded-xl overflow-hidden shadow-2xl
        bg-[#061530] border border-[#0a2560]
        ring-1 ${s.ring}
        transition-all duration-300 ease-out
        ${visible && !leaving
          ? 'opacity-100 translate-x-0'
          : 'opacity-0 -translate-x-8'
        }
      `}
    >
      {/* Colour bar */}
      <div className={`absolute top-0 left-0 w-1 h-full ${s.bar}`} />

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/5">
        <div
          className={`h-full ${s.bar} opacity-60`}
          style={{
            animation: `toast-shrink ${duration}ms linear forwards`,
          }}
        />
      </div>

      <div className="flex items-start gap-3 px-4 py-3 pl-5">
        {s.icon}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white leading-snug">{toast.title}</p>
          {toast.message && (
            <p className="text-xs text-blue-300 mt-0.5 leading-relaxed break-words">{toast.message}</p>
          )}
        </div>
        <button
          onClick={dismiss}
          className="flex-shrink-0 text-blue-400 hover:text-white transition-colors p-0.5"
        >
          <X size={13} />
        </button>
      </div>
    </div>
  );
}

// ─── Container ────────────────────────────────────────────────────────────────

function ToastContainer({ toasts, onRemove }: { toasts: Toast[]; onRemove: (id: number) => void }) {
  if (toasts.length === 0) return null;
  return (
    <div className="fixed bottom-5 left-5 z-[9999] flex flex-col gap-2.5 items-start pointer-events-none">
      {toasts.map(t => (
        <div key={t.id} className="pointer-events-auto">
          <ToastCard toast={t} onRemove={onRemove} />
        </div>
      ))}
    </div>
  );
}

// ─── Provider ─────────────────────────────────────────────────────────────────

let _nextId = 1;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((
    title: string,
    message: string,
    type: ToastType = 'error',
    duration?: number,
  ) => {
    const id = _nextId++;
    setToasts(prev => {
      // Max 5 toast at once — drop oldest
      const next = [...prev, { id, title, message, type, duration }];
      return next.slice(-5);
    });
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={remove} />
      {/* Keyframe animation injected once */}
      <style>{`
        @keyframes toast-shrink {
          from { width: 100%; }
          to   { width: 0%; }
        }
      `}</style>
    </ToastContext.Provider>
  );
}
